import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  generateImage,
  suggestCopy,
  InsufficientCreditsError,
  GenerationTimeoutError,
  NetworkError,
  ContentRejectedError,
  TransientUpstreamError,
  type Quality,
  type Provider,
} from '@/lib/api';
import { type AspectRatio, type SuggestedFrame, aspectClass } from '../lib/composeRender';
import { savePost } from '@/lib/gallery';
import type { PlanTier } from '@/hooks/useCredits';

export type { SuggestedFrame };

interface StepPreviewProps {
  productFile: File | null;
  /** Optional ordered array of product files for multi-product carousels.
   *  When supplied with length > 1, EACH carousel frame may use a
   *  different product per the cyclic mapping below. When omitted (or
   *  length ≤ 1), behavior is unchanged from the single-file pre-May-27
   *  path — every frame uses `productFile`. Per-frame routing:
   *    1 product  → frames 1,2,3,4 = product 1
   *    2 products → frames 1,2 = product 1; frames 3,4 = product 2
   *    3 products → frame 1=1, 2=2, 3=3, 4=1  (cycles back to first)
   *    4 products → 1:1 mapping
   */
  productFiles?: File[];
  /** Multi-product carousel — per-product names parallel to
   *  productFiles. When supplied with length matching productFiles,
   *  each frame's /api/generate call uses the matching product's
   *  name. Falls back to the singular `productName` prop when not
   *  set (or when a particular index has an empty string). */
  productNames?: string[];
  /** Multi-product carousel — per-product descriptions parallel to
   *  productFiles. Same fallback rules as productNames above. */
  productDescriptions?: string[];
  platformId: string;
  platformAspectRatio: string;
  templateId: string;
  productName: string;
  productDescription: string;
  quality: Quality;
  onQualityChange: (q: Quality) => void;
  /** Image-gen provider selected in Step 5. Threads through to /api/generate
   *  so the backend routes the request. Default 'openai'. */
  provider: Provider;
  /** Fashion route only — auto-detected garment region. Passed through to
   *  /api/generate so the backend uses the matching mask. Undefined for
   *  the default (template-based) route. */
  garmentType?: 'top' | 'bottoms' | 'shoes';
  isCarousel: boolean;
  planTier: PlanTier;
  generatedImages: string[];
  suggestedFrames: SuggestedFrame[];
  lastGenKey: string | null;
  onGenerationComplete: (images: string[], frames: SuggestedFrame[], key: string) => void;
  onCostAdd: (delta: number) => void;
  /** Called after any generation attempt so the credit balance pill refreshes
      (success → balance dropped; failure → balance refunded back). */
  onCreditsChanged: () => void;
  onBack: () => void;
  onContinue: () => void;
}

// All aspect ratios our backend can handle (matches backend's SIZE_MAP).
// Unknown aspects fall back to 1:1 so the wizard never dead-ends — better
// to render at a defined aspect than to refuse.
const SUPPORTED_RATIOS: ReadonlySet<string> = new Set([
  '1:1', '4:5', '2:3', '9:16', '1.91:1', '16:9',
]);

const QUALITY_MULTIPLIER: Record<Quality, number> = { low: 1, medium: 2, high: 4 };
// User-facing labels for the 3 quality tiers. Backend Quality IDs stay
// 'low'/'medium'/'high' — these are just the strings shown to users.
// Must stay in sync with StepQuality.tsx's TIERS array.
const QUALITY_LABEL: Record<Quality, string> = { low: 'Regular', medium: 'Enhanced', high: 'Studio' };

// Wall-clock duration we *expect* a single frame to take at each quality.
// Real-world ranges: Low 10–20s, Medium 20–40s, High 45–90s. The progress
// formula uses this as the time at which the bar should read ~80% — see
// runGeneration() for the math. Erring conservatively (slower) is safer
// than erring fast, because a fast estimate makes the bar stall near 100%
// while a slow estimate just means the user is pleasantly surprised when
// it finishes early.
const EXPECTED_MS_PER_FRAME: Record<Quality, number> = {
  low: 18_000,
  medium: 35_000,
  high: 70_000,
};

// Minimum time between visible carousel-tile flips. OpenAI returns parallel
// results in a tight cluster (within ~1-2s of each other for same-quality
// calls), which collapses the visual "tiles popping in" effect. Staggering
// the flips by 800ms guarantees the user sees each frame light up
// individually. Cost: at worst ~2.4s of extra wait at the end of a
// 4-frame run before transitioning to the viewer.
const FRAME_FLIP_GAP_MS = 800;
// After the last tile flips, hold the grid view briefly so the user gets
// to see all 4 ready tiles before the page transitions to the single-image
// viewer. Without this, the last flip and the viewer swap happen in the
// same React commit and the last tile is never visibly "ready" on screen.
const FINAL_HOLD_MS = 1_400;

/** Maps any thrown error to: title + body + recommended retry behavior.
 *  Centralized so the error pane (and any future toast) renders consistently
 *  without sprinkling instanceof checks through the JSX. */
type ErrorView = {
  title: string;
  body: string;
  icon: string;        // remixicon class
  tone: 'warn' | 'fail' | 'info';
  /** 'retry' lets the user click "Try Again" (same params). 'upgrade' routes
   *  to /checkout. 'edit' tells them to go back and change inputs. */
  action: 'retry' | 'upgrade' | 'edit';
};

/** Parse an aspect ratio string ("1:1", "2:3", "1.91:1") to a width/height
 *  decimal — used by the preview container's maxWidth so the box stays
 *  proportional when max-height is the binding constraint. Returns 1
 *  (square) for malformed input, which keeps layout sane on bad data. */
function aspectFraction(aspect: string): number {
  const [w, h] = aspect.split(':').map(parseFloat);
  if (!Number.isFinite(w) || !Number.isFinite(h) || h === 0) return 1;
  return w / h;
}

function describeError(err: unknown): ErrorView {
  if (err instanceof InsufficientCreditsError) {
    return {
      title: 'Out of credits',
      body: 'You don\'t have enough credits to generate this post. Upgrade your plan to keep creating — your inputs are saved.',
      icon: 'ri-copper-coin-line',
      tone: 'warn',
      action: 'upgrade',
    };
  }
  if (err instanceof GenerationTimeoutError) {
    return {
      title: 'OpenAI took too long',
      body: 'Generation timed out and your credits were refunded. OpenAI is occasionally slow at peak times — try again in a moment.',
      icon: 'ri-time-line',
      tone: 'warn',
      action: 'retry',
    };
  }
  if (err instanceof NetworkError) {
    return {
      title: 'Can\'t reach our server',
      body: 'We couldn\'t connect to the backend. Check your internet, then try again. No credits were charged.',
      icon: 'ri-wifi-off-line',
      tone: 'fail',
      action: 'retry',
    };
  }
  if (err instanceof TransientUpstreamError) {
    return {
      title: 'Briefly rate-limited',
      body: 'OpenAI is throttling us right now. Wait 10–20 seconds and try again — no credits were charged.',
      icon: 'ri-hourglass-line',
      tone: 'info',
      action: 'retry',
    };
  }
  if (err instanceof ContentRejectedError) {
    return {
      title: 'OpenAI rejected the input',
      body: (err as Error).message ||
        'OpenAI\'s safety filter blocked this generation. Try a different product photo or rephrase the description.',
      icon: 'ri-shield-cross-line',
      tone: 'fail',
      action: 'edit',
    };
  }
  return {
    title: 'Something went wrong',
    body: err instanceof Error ? err.message : 'Generation failed unexpectedly. Please try again.',
    icon: 'ri-error-warning-line',
    tone: 'fail',
    action: 'retry',
  };
}

export default function StepPreview({
  productFile,
  productFiles,
  productNames,
  productDescriptions,
  platformId,
  platformAspectRatio,
  templateId,
  productName,
  productDescription,
  quality,
  onQualityChange,
  provider,
  garmentType,
  isCarousel,
  planTier,
  generatedImages,
  suggestedFrames,
  lastGenKey,
  onGenerationComplete,
  onCostAdd,
  onCreditsChanged,
  onBack,
  onContinue,
}: StepPreviewProps) {
  const navigate = useNavigate();
  const isSupportedRatio = SUPPORTED_RATIOS.has(platformAspectRatio);
  const aspectRatio: AspectRatio = (isSupportedRatio ? platformAspectRatio : '1:1') as AspectRatio;
  const frameCount = isCarousel ? 4 : 1;
  const isFree = planTier === 'free';

  const [generating, setGenerating] = useState(false);
  // Stores the raw thrown error so the error pane can render a typed view.
  // null = no error this run.
  const [generationError, setGenerationError] = useState<unknown>(null);
  // Smooth 0–100 percentage. Driven by an interval timer below; not just the
  // integer count of completed frames (which would freeze at 0 for a full
  // 60s on a single-frame High generation).
  const [progress, setProgress] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showRegenMenu, setShowRegenMenu] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Dollar cost of the MOST RECENT generation (not lifetime). Resets at the
  // start of each runGeneration; accumulates from genPromises + suggestCopy.
  // null = no generation has completed yet (e.g. on cold-cache entry into
  // step 6). Distinct from the parent's lifetime `costSpent` accumulator —
  // we want to show "this version cost you X" not "this session cost X".
  const [lastGenCost, setLastGenCost] = useState<number | null>(null);
  // Mutable counter shared between the genPromises and the tick interval —
  // tick reads it on every fire to compute "completion-based" progress
  // without re-rendering on every read.
  const completedFramesRef = useRef(0);
  // Per-frame state for the carousel loader. Each entry corresponds to one
  // parallel generateImage call. Updated incrementally as each promise
  // resolves so the UI can flip individual tiles from shimmer → image as
  // they come back, instead of all 4 popping in at once at the end.
  type FrameStatus = 'loading' | 'done' | 'error';
  const [frameStatuses, setFrameStatuses] = useState<FrameStatus[]>([]);
  const [frameImagesProgressive, setFrameImagesProgressive] = useState<
    (string | null)[]
  >([]);
  // Wall-clock timestamp of the latest scheduled tile flip. We enforce a
  // minimum gap between flips (FRAME_FLIP_GAP_MS below) so even though
  // OpenAI returns all 4 parallel results in a tight cluster (within ~1-2s
  // of each other), the user visibly sees frames lighting up one at a time.
  // Read by both the genPromise tile flips AND the post-Promise.all hold so
  // we don't transition to the viewer until the last tile has had its
  // moment on screen.
  const lastFlipAtRef = useRef(0);

  // Resolved per-frame product files. Falls back to a length-1 array
  // wrapping the single productFile when productFiles isn't passed (the
  // common path for non-carousel routes). Carousel route's parent
  // passes the full ordered list of 1–4 picks.
  const filesForFrames: File[] = (() => {
    if (productFiles && productFiles.length > 0) return productFiles;
    return productFile ? [productFile] : [];
  })();

  // Map a 0-indexed frame slot (0..frameCount-1) to its product INDEX
  // per the spec locked May 27. n is the number of unique products
  // selected; the mapping is intentional, not modulo:
  //   n=1 → all frames use product 0
  //   n=2 → first half = product 0, second half = product 1
  //   n=3 → frames 0,1,2 = products 0,1,2; frame 3 wraps to product 0
  //   n=4 → 1:1
  // Returns -1 when n === 0 (no products).
  const mapFrameToProductIdx = (frameIdx: number): number => {
    const n = filesForFrames.length;
    if (n === 0) return -1;
    if (n === 1) return 0;
    if (n === 2) return frameIdx < 2 ? 0 : 1;
    if (n === 3) return frameIdx === 3 ? 0 : Math.min(frameIdx, n - 1);
    return Math.min(frameIdx, n - 1); // n === 4 → 1:1; defensive on out-of-range
  };

  // Convenience — direct file lookup using the index mapping. Used by
  // the generation loop below.
  const mapFrameToProduct = (frameIdx: number): File | null => {
    const idx = mapFrameToProductIdx(frameIdx);
    return idx >= 0 ? filesForFrames[idx] ?? null : null;
  };

  // Cache key now includes ALL product file identities so multi-product
  // carousels don't share a cache slot with a single-product run of the
  // same template+platform. Sorted-stable join: order matters because
  // frame-to-product mapping depends on selection order. Also folds in
  // the per-product metadata arrays (when present) so editing one
  // product's name/desc invalidates the cache for the whole batch.
  const filesKey = filesForFrames.map((f) => `${f.name}:${f.size}`).join('|');
  const metadataKey = (productNames && productNames.length > 0)
    ? `m:${productNames.join('|')}|${(productDescriptions ?? []).join('|')}`
    : `${productName}|${productDescription}`;
  const currentKey = filesForFrames.length > 0
    ? `${filesKey}|${templateId}|${platformId}|${aspectRatio}|${metadataKey}|${quality}|${provider}|${garmentType ?? ''}`
    : '';

  const runGeneration = useCallback(async () => {
    if (filesForFrames.length === 0 || !templateId) return;
    setGenerating(true);
    setGenerationError(null);
    setProgress(0);
    setLastGenCost(null);
    completedFramesRef.current = 0;
    setFrameStatuses(Array(frameCount).fill('loading'));
    setFrameImagesProgressive(Array(frameCount).fill(null));
    lastFlipAtRef.current = 0;
    // Local accumulator so we don't have to round-trip through React state
    // for every fractional-cent addition during the run. Final value flushed
    // to `lastGenCost` on success below.
    let runCost = 0;

    // ---- Progress estimator ----
    // Two strategies based on frame count, both clamped to 99 so the snap
    // to 100 on actual resolution is meaningful:
    //
    // SINGLE FRAME (`isCarousel === false`):
    //   Pure time-based exponential approach:
    //     progress(t) = 100 * (1 - exp(-t / tau))
    //   tau chosen so progress(expectedDuration) == 80%.
    //   Always moving; never claims 100% (asymptote).
    //
    // MULTI FRAME (carousel):
    //   The 4 frames run in parallel — they tend to complete in a tight
    //   cluster near the end, so a pure time-based formula reads "0/4
    //   frames · 99%" for most of the run, which is a lie.
    //   Honest model instead: bar = warmup zone + per-frame slots.
    //     - 25% reserved for a time-based "warming up" indicator that
    //       saturates quickly (so the bar moves immediately on start).
    //     - Remaining 75% split into N equal slots (one per frame); each
    //       completed frame jumps the bar by one slot.
    //   This means the visible % can't overshoot what's actually been
    //   accomplished. If a run fails at the network layer with 0/4 done,
    //   the user sees ~25%, not 99%.
    const start = Date.now();
    const expectedDuration = EXPECTED_MS_PER_FRAME[quality] * (isCarousel ? 1.4 : 1);
    const TAU = expectedDuration / Math.log(5);  // ≈ expected / 1.609
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - start;
      let next: number;
      if (isCarousel) {
        const WARMUP_CAP = 25;
        const PER_FRAME = (100 - WARMUP_CAP) / frameCount;
        const completionBased = completedFramesRef.current * PER_FRAME;
        // TAU/2 makes the warmup saturate ~2x faster than the single-frame
        // formula would, so the user sees obvious motion in the first ~10s.
        const timeBased = WARMUP_CAP * (1 - Math.exp(-elapsed / (TAU / 2)));
        next = completionBased + timeBased;
      } else {
        next = 100 * (1 - Math.exp(-elapsed / TAU));
      }
      setProgress(Math.min(99, next));
    }, 200);

    const frameNumbers = Array.from({ length: frameCount }, (_, i) => i + 1);
    const genPromises = frameNumbers.map(async (frameNumber) => {
      try {
        // Multi-product carousel: pick the right file + metadata for
        // THIS frame (cyclic mapping). For single-product modes
        // filesForFrames is length-1 so every frame gets the same
        // file AND the global productName/Description are used —
        // preserves pre-May-27 behavior identically.
        const frameIdx = frameNumber - 1;
        const productIdx = mapFrameToProductIdx(frameIdx);
        const frameProductFile = productIdx >= 0
          ? (filesForFrames[productIdx] ?? productFile)
          : productFile;
        if (!frameProductFile) throw new Error('No product file for this frame');
        // Per-product name/description fall back to the global
        // singular props when the parallel array isn't supplied OR
        // when a specific entry is empty (single-product routes
        // always fall through to the global ones).
        const frameName = (productNames && productIdx >= 0 && productNames[productIdx])
          ? productNames[productIdx]
          : productName;
        const frameDescription = (productDescriptions && productIdx >= 0 && productDescriptions[productIdx])
          ? productDescriptions[productIdx]
          : productDescription;
        const result = await generateImage({
          productFile: frameProductFile,
          templateId,
          frameNumber,
          aspectRatio,
          productName: frameName,
          productDescription: frameDescription,
          quality,
          provider,
          garmentType,
        });
        onCostAdd(result.cost);
        runCost += result.cost;
        // Schedule the visible tile flip with a stagger. OpenAI returns
        // parallel results in a tight cluster, so if we flipped immediately
        // here all 4 tiles would visually pop in within ~2s. Instead we
        // enforce FRAME_FLIP_GAP_MS between flips: each new flip is
        // scheduled at least that much after the previous one (regardless
        // of which frame_number finished first). User sees 4 distinct
        // "tile lights up" moments.
        const idx = frameNumber - 1;
        const now = Date.now();
        const flipAt = Math.max(now, lastFlipAtRef.current + FRAME_FLIP_GAP_MS);
        lastFlipAtRef.current = flipAt;
        window.setTimeout(() => {
          setFrameStatuses((prev) => prev.map((s, i) => (i === idx ? 'done' : s)));
          setFrameImagesProgressive((prev) =>
            prev.map((u, i) => (i === idx ? result.imageUrl : u)),
          );
          completedFramesRef.current += 1;
        }, flipAt - now);
        return result.imageUrl;
      } catch (err) {
        const idx = frameNumber - 1;
        setFrameStatuses((prev) => prev.map((s, i) => (i === idx ? 'error' : s)));
        throw err;
      }
    });

    // Copy suggestion is fire-and-forget for the *generation* status — if
    // it fails we still show the images. Errors here only end up in the
    // console; they don't poison the run.
    // Multi-product carousel note: suggestCopy generates a single set of
    // headlines/taglines based on ONE product file. For multi-product
    // carousels we pass the FIRST product — the resulting copy is shared
    // across all frames. Users can regenerate per-frame in StepText if
    // they want per-product copy. v2 could fan out one suggest-copy
    // call per unique product file; deferred.
    const suggestProductFile = filesForFrames[0] ?? productFile!;
    const suggestPromise = suggestCopy({
      productFile: suggestProductFile,
      productName,
      productDescription,
      frameCount,
    })
      .then((result) => {
        const c = result._cost ?? 0;
        onCostAdd(c);
        runCost += c;
        return result.frames;
      })
      .catch((err) => {
        console.error('suggestCopy failed:', err);
        return [] as SuggestedFrame[];
      });

    try {
      const [images, frames] = await Promise.all([Promise.all(genPromises), suggestPromise]);
      // For carousels, the genPromises have queued tile flips via setTimeout
      // (staggered by FRAME_FLIP_GAP_MS). At this point Promise.all is done
      // but the LAST flip may still be pending — and even after it fires we
      // want a brief moment where all 4 tiles read "Ready" before the page
      // transitions to the single-image viewer. Wait for both.
      if (isCarousel) {
        const now = Date.now();
        const lastFlipDone = lastFlipAtRef.current; // wall-clock ms when last flip fires
        const transitionAt = Math.max(now, lastFlipDone) + FINAL_HOLD_MS;
        await new Promise<void>((resolve) =>
          window.setTimeout(resolve, transitionAt - now),
        );
      }
      setProgress(100);
      // Only commit the cost on success — a failed run gets refunded by the
      // backend, so showing "this cost you $0.05" for a failed run would be
      // a lie. On error path, lastGenCost stays null and the badge hides.
      setLastGenCost(runCost);
      onGenerationComplete(images, frames, currentKey);
    } catch (err) {
      // Don't crash the page on ANY error — every thrown shape here is
      // already mapped to a typed Error in api.ts, and describeError() in
      // the render path turns it into a friendly pane. Just stash it.
      console.error('generate failed:', err);
      setGenerationError(err);
    } finally {
      window.clearInterval(tick);
      setGenerating(false);
      onCreditsChanged();
    }
    // filesKey captures the full identity of all selected products
    // (name + size joined by '|'). Including it ensures the callback
    // identity changes when the user adds/removes products mid-flow
    // OR reorders them (frame mapping is order-sensitive).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesKey, templateId, frameCount, aspectRatio, productName, productDescription, quality, provider, garmentType, isCarousel, currentKey, onCostAdd, onGenerationComplete, onCreditsChanged]);

  // Auto-trigger when entering with a stale or missing cache. The cache key
  // includes quality, so quality changes flow through here automatically.
  const triggeredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (filesForFrames.length === 0 || !templateId) return;
    if (triggeredKeyRef.current === currentKey) return;
    if (currentKey === lastGenKey && generatedImages.length === frameCount) {
      triggeredKeyRef.current = currentKey;
      return;
    }
    triggeredKeyRef.current = currentKey;
    runGeneration();
  }, [currentKey, lastGenKey, generatedImages.length, frameCount, filesKey, templateId, runGeneration]);

  void suggestedFrames;

  // Regenerate at chosen quality. Same quality → force re-run (cache key
  // unchanged, useEffect won't fire). Different quality → onQualityChange
  // updates parent, useEffect re-fires on the new cache key.
  const handleRegenerateWithQuality = (q: Quality) => {
    setShowRegenMenu(false);
    if (q === quality) {
      triggeredKeyRef.current = null;
      runGeneration();
    } else {
      onQualityChange(q);
    }
  };

  // ---- Download ----
  // Free tier sees an "Upgrade to Download" CTA routing to /checkout instead.
  // Defense-in-depth — even if the button were hit, no real export happens
  // because the free-tier render path swaps it out below.
  const handleDownload = () => {
    const imgSrc = generatedImages[activeImageIndex];
    if (!imgSrc) return;
    const link = document.createElement('a');
    link.href = imgSrc;
    link.download = `post-${templateId}-frame${activeImageIndex + 1}.png`;
    link.click();
  };

  // ---- Save to Gallery ----
  const handleSaveToGallery = async () => {
    if (saveStatus === 'saving') return;
    const imgSrc = generatedImages[activeImageIndex];
    if (!imgSrc) return;
    setSaveStatus('saving');
    try {
      // Fetch the data-url / blob-url back as a Blob — no overlays at this
      // step so we don't need to flatten with text.
      const resp = await fetch(imgSrc);
      const blob = await resp.blob();
      const frame = suggestedFrames[activeImageIndex];
      await savePost({
        blob,
        templateId,
        aspectRatio,
        headline: frame?.headline ?? null,
        tagline: frame?.tagline ?? null,
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err) {
      console.error('Save to Gallery failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 4000);
    }
  };

  // "Cache stale" check — when the props have changed since the last
  // successful gen (different template, different products, different
  // metadata, etc.), but runGeneration hasn't yet flipped `generating`
  // to true, the render path would otherwise briefly show the PREVIOUS
  // run's images alongside the new templateId. Treat that one-frame
  // gap as "still loading" so the user only ever sees the loader
  // between picks, never the stale output. Verified May 27 — user
  // reported "template 25 images flashed before all switched to
  // template 26" which traced to this exact transition window.
  const cacheStale = lastGenKey != null && lastGenKey !== currentKey;
  const effectiveGenerating = generating || cacheStale;
  // Synthesize all-loading state for the brief stale-but-not-yet-
  // started window so the 2x2 carousel grid + single-frame loader
  // show shimmers instead of stale 'done' tiles from the previous run.
  const displayFrameStatuses: FrameStatus[] = (cacheStale && !generating)
    ? Array(frameCount).fill('loading' as FrameStatus)
    : frameStatuses;
  const displayFrameImages: (string | null)[] = (cacheStale && !generating)
    ? Array(frameCount).fill(null)
    : frameImagesProgressive;
  // Active image / ready check both gate on the cache being current
  // — keeps the editing UI from showing stale results too.
  const activeImage = (cacheStale ? '' : generatedImages[activeImageIndex]) || '';
  const readyForEditing = generatedImages.length === frameCount && !effectiveGenerating;
  // `progress` is already 0–100 (smooth). Round for display.
  const progressPct = Math.round(progress);
  const errorView = generationError ? describeError(generationError) : null;

  return (
    <div className="h-full flex flex-col">
      {/* Content panel — matches the white wizard panel that wraps steps
          1-5 in app/page.tsx (we opt out of the auto-wrap there for step 6
          since the footer needs to sit OUTSIDE this panel, as a separate
          panel below — same visual treatment as steps 1-5). */}
      <div className="flex-1 min-h-0 bg-surface-panel rounded-2xl border-2 border-white p-6 overflow-y-auto shadow-lg shadow-primary/15 flex flex-col">
        {/* Compact status banner instead of a full StepHeader. The sidebar
            already says "Generation" — repeating the section title + icon
            here was visual redundancy. This is just a one-line status
            (generating / ready / error) so the user knows where they are
            in the flow without a second header. */}
        <div className="flex-shrink-0 mb-4 pb-3 -mx-6 px-6 border-b-2 border-white">
          <p className="font-display font-bold text-dark text-sm md:text-base">
            {generating
              ? 'Generating your post…'
              : errorView
                ? errorView.title
                : 'Your post is ready'}
          </p>
          <p className="text-dark-muted text-xs md:text-sm leading-snug mt-0.5">
            {generating
              ? `AI is blending your product into the selected scene at ${QUALITY_LABEL[quality]} quality.`
              : errorView
                ? errorView.body
                : 'Preview each frame, download it as-is, save to My Creatives, or continue to add text overlays.'}
          </p>
        </div>

      {effectiveGenerating ? (
        // ---- Loading state ----
        // Two layouts:
        //
        // 1. Single frame: one big loader card (gradient + shimmer + sparkles +
        //    percentage ring). The user has nothing else to watch.
        //
        // 2. Carousel (frameCount > 1): a 2×2 grid of tiles, one per parallel
        //    generateImage call. Each tile shimmers while its frame is in
        //    flight and flips to the actual image (with a "Ready" badge) the
        //    instant its promise resolves — even though Promise.all is still
        //    awaiting the slowest sibling. Users see frames pop in
        //    individually instead of all 4 appearing at once at the end.
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-5 px-2">
          {isCarousel && displayFrameStatuses.length === frameCount ? (
            // ---- Carousel: per-frame tile grid ----
            <div
              className="grid grid-cols-2 gap-3 w-full"
              style={{
                // The 2×2 grid's overall aspect matches each tile's aspect
                // (2 cols × 2 rows of identical cells → same ratio). Match the
                // single-frame loader's sizing so the panel doesn't reflow
                // between the two layouts.
                maxWidth: `calc((100vh - 260px) * ${aspectFraction(aspectRatio)})`,
                maxHeight: 'calc(100vh - 260px)',
              }}
            >
              {displayFrameStatuses.map((status, i) => {
                const img = displayFrameImages[i];
                return (
                  <div
                    key={i}
                    className={`relative rounded-xl overflow-hidden border border-surface-dark/40 bg-surface ${aspectClass(aspectRatio)}`}
                  >
                    {status === 'done' && img ? (
                      <>
                        <img
                          src={img}
                          alt={`Frame ${i + 1}`}
                          className="w-full h-full object-cover"
                          style={{ animation: 'sp-fadein 400ms ease-out both' }}
                          draggable={false}
                        />
                        <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold shadow">
                          <i className="ri-check-line text-xs" />
                          Ready
                        </div>
                      </>
                    ) : status === 'error' ? (
                      <div className="w-full h-full bg-red-50 flex flex-col items-center justify-center gap-1 text-red-500">
                        <i className="ri-error-warning-line text-2xl" />
                        <p className="text-[11px] font-semibold">Frame {i + 1} failed</p>
                      </div>
                    ) : (
                      <>
                        {/* Drifting gradient backdrop */}
                        <div
                          className="absolute inset-0 opacity-60"
                          style={{
                            background:
                              'linear-gradient(135deg, rgba(244,114,182,0.18), rgba(99,102,241,0.18), rgba(56,189,248,0.18), rgba(244,114,182,0.18))',
                            backgroundSize: '300% 300%',
                            animation: 'sp-bgshift 8s ease-in-out infinite',
                          }}
                        />
                        {/* Shimmer sweep */}
                        <div
                          className="absolute inset-0"
                          style={{
                            background:
                              'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
                            backgroundSize: '200% 100%',
                            animation: 'sp-shimmer 2.4s linear infinite',
                          }}
                        />
                        {/* Center: sparkle + frame label */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-center pointer-events-none">
                          <i
                            className="ri-sparkling-2-fill text-primary text-3xl"
                            style={{ animation: 'sp-spin 4s linear infinite, sp-twinkle 1.6s ease-in-out infinite' }}
                          />
                          <p className="font-display font-bold text-dark text-sm">
                            Frame {i + 1}
                          </p>
                          <p className="text-dark-muted text-[11px]">Generating…</p>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
          <div
            // Single-frame loader: same sizing math as the final-render
            // preview below so the skeleton occupies the same area the image
            // will fill when generation completes — no jarring resize on
            // transition.
            className={`relative rounded-2xl overflow-hidden border border-surface-dark/40 w-full max-w-full max-h-[calc(100vh-260px)] ${aspectClass(aspectRatio)}`}
            style={{
              maxWidth: `calc((100vh - 260px) * ${aspectFraction(aspectRatio)})`,
            }}
          >
            {/* Layer 1: drifting gradient backdrop */}
            <div
              className="absolute inset-0 opacity-60"
              style={{
                background:
                  'linear-gradient(135deg, rgba(244,114,182,0.18), rgba(99,102,241,0.18), rgba(56,189,248,0.18), rgba(244,114,182,0.18))',
                backgroundSize: '300% 300%',
                animation: 'sp-bgshift 8s ease-in-out infinite',
              }}
            />
            {/* Layer 2: shimmer sweep */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
                backgroundSize: '200% 100%',
                animation: 'sp-shimmer 2.4s linear infinite',
              }}
            />
            {/* Layer 3: floating sparkle particles. Six pre-positioned dots
                with slightly different animation durations/delays so they
                don't all bob in sync. Pointer-events-none so they don't
                interfere with anything beneath. */}
            <div className="absolute inset-0 pointer-events-none">
              {[
                { top: '15%', left: '20%', size: 8, delay: '0s', dur: '3.2s' },
                { top: '25%', left: '78%', size: 6, delay: '0.6s', dur: '2.8s' },
                { top: '60%', left: '12%', size: 10, delay: '1.1s', dur: '3.6s' },
                { top: '70%', left: '85%', size: 7, delay: '0.3s', dur: '3.0s' },
                { top: '40%', left: '90%', size: 5, delay: '1.4s', dur: '2.5s' },
                { top: '85%', left: '45%', size: 9, delay: '0.9s', dur: '3.4s' },
              ].map((p, i) => (
                <i
                  key={i}
                  className="ri-sparkling-2-fill text-primary absolute"
                  style={{
                    top: p.top,
                    left: p.left,
                    fontSize: `${p.size * 2}px`,
                    animation: `sp-float ${p.dur} ease-in-out ${p.delay} infinite, sp-twinkle ${p.dur} ease-in-out ${p.delay} infinite`,
                    opacity: 0.7,
                  }}
                />
              ))}
            </div>
            {/* Layer 4+5: center cluster — percentage ring + sparkle */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
              <div className="relative w-28 h-28">
                {/* Gradient stroke definition (used by the progress arc) */}
                <svg className="w-28 h-28 -rotate-90" viewBox="0 0 112 112">
                  <defs>
                    <linearGradient id="sp-ring" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#f472b6" />
                      <stop offset="50%" stopColor="#6366f1" />
                      <stop offset="100%" stopColor="#38bdf8" />
                    </linearGradient>
                  </defs>
                  <circle cx="56" cy="56" r="48" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="8" />
                  <circle
                    cx="56"
                    cy="56"
                    r="48"
                    fill="none"
                    stroke="url(#sp-ring)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 48}
                    strokeDashoffset={2 * Math.PI * 48 * (1 - progress / 100)}
                    style={{ transition: 'stroke-dashoffset 400ms ease-out' }}
                  />
                </svg>
                {/* Center sparkle — slow rotate + pulse so the ring isn't
                    static during the long stretches when % hasn't moved. */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <i
                    className="ri-sparkling-2-fill text-primary text-3xl"
                    style={{ animation: 'sp-spin 4s linear infinite, sp-twinkle 1.6s ease-in-out infinite' }}
                  />
                </div>
              </div>
              <div className="text-center">
                <p className="font-display font-bold text-dark text-2xl tabular-nums">{progressPct}%</p>
                <p className="text-dark-muted text-xs mt-0.5">AI is crafting your post</p>
              </div>
            </div>
          </div>
          )}

          {/* Footer metadata line + horizontal progress bar. Shared by both
              the single-frame and carousel layouts. For carousels, the
              per-frame count is read from `frameStatuses` (reactive) instead
              of the ref so it updates in lockstep with the tile flips above. */}
          <div className="w-full max-w-2xl flex flex-col gap-2">
            <div className="h-1.5 bg-surface-dark/20 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progressPct}%`,
                  background: 'linear-gradient(90deg, #f472b6, #6366f1, #38bdf8)',
                  transition: 'width 400ms ease-out',
                }}
              />
            </div>
            <p className="text-dark-muted text-xs text-center">
              {isCarousel
                ? frameStatuses.filter((s) => s === 'done').length
                : completedFramesRef.current}
              /{frameCount} frame{frameCount > 1 ? 's' : ''} · {QUALITY_LABEL[quality]} quality · typically {quality === 'high' ? '60–90s' : quality === 'medium' ? '20–40s' : '10–20s'} per frame
            </p>
          </div>

          {/* Keyframes for all the animations above. Scoped via <style> tag
              so we don't have to touch the global Tailwind config. */}
          <style>{`
            @keyframes sp-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
            @keyframes sp-bgshift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
            @keyframes sp-float   { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
            @keyframes sp-twinkle { 0%,100% { opacity: 0.35; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.15); } }
            @keyframes sp-spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            @keyframes sp-fadein  { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
          `}</style>
        </div>
      ) : errorView ? (
        // ---- Error pane — typed view from describeError(). Tone drives
        //      color, action drives the primary CTA. We never hide what went
        //      wrong; we just frame it for the user's next action. ----
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-5 px-4">
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
              errorView.tone === 'fail'
                ? 'bg-red-50 text-red-500'
                : errorView.tone === 'warn'
                ? 'bg-amber-50 text-amber-600'
                : 'bg-sky-50 text-sky-600'
            }`}
          >
            <i className={`${errorView.icon} text-3xl`} />
          </div>
          <div className="text-center max-w-md">
            <p className="font-display font-bold text-dark text-lg mb-1">{errorView.title}</p>
            <p className="text-dark-muted text-sm leading-relaxed">{errorView.body}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-dark hover:bg-surface transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Back
            </button>
            {errorView.action === 'upgrade' ? (
              <button
                onClick={() => navigate('/checkout')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-sm font-bold transition-colors cursor-pointer shadow-lg shadow-amber-500/20"
              >
                <i className="ri-vip-crown-line" />
                Upgrade plan
              </button>
            ) : errorView.action === 'edit' ? (
              <button
                onClick={onBack}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors cursor-pointer"
              >
                <i className="ri-edit-line" />
                Change inputs
              </button>
            ) : (
              <button
                onClick={runGeneration}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" />
                Try Again
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4 px-2">
            <div
              // Sizing: fill the available area within the panel, capped by
              // viewport height so the image is always fully visible on
              // every breakpoint without scrolling.
              //
              // The maxWidth inline style keeps the container's aspect
              // ratio when max-h is the binding constraint. Without it,
              // `w-full + max-h-*` lets width = parent (e.g. 1140px) while
              // height is clamped at 820px — that stretches a "square"
              // container into 1140x820 (1.39:1) and any 1:1 image inside
              // gets cover-cropped (head/feet clipped). Capping
              // maxWidth = max-h * aspectFraction keeps the container
              // proportional regardless of how wide the parent is.
              //
              // calc(100vh - 260px) leaves room for: 96px header + ~80px
              // step header + ~80px footer + paddings/margins.
              className={`relative rounded-2xl overflow-hidden border border-surface-dark/40 bg-surface shadow-sm w-full max-w-full max-h-[calc(100vh-260px)] ${aspectClass(aspectRatio)}`}
              style={{
                maxWidth: `calc((100vh - 260px) * ${aspectFraction(aspectRatio)})`,
              }}
            >
              {activeImage ? (
                // object-contain (Aspect Fit) — show the whole image even
                // if its native aspect doesn't match the container's
                // (which it normally does, but object-contain is the
                // safer default since we never want to hide pixels in
                // a preview the user is about to download).
                <img src={activeImage} alt="Generated post" className="w-full h-full object-contain" draggable={false} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <p className="text-dark-muted text-sm">No image</p>
                </div>
              )}
              {/* Cost badge — overlaid bottom-left on the preview image so
                  it's unmissable but doesn't take its own row. Only renders
                  when a generation has actually completed (lastGenCost
                  non-null). 4-decimal precision because Low at 1:1 is
                  ~$0.005 — 2 decimals would round to $0.01 and exaggerate.
                  Label varies by provider: OpenAI shows the quality tier,
                  Gemini just shows "Gemini" (single quality, no tier). */}
              {/* Diagnostic only — hidden in production (kept for local
                  cost reconciliation). */}
              {import.meta.env.DEV && lastGenCost !== null && (
                <div
                  title={
                    provider === 'gemini'
                      ? `Gemini API cost for this generation (${frameCount} frame${frameCount > 1 ? 's' : ''})`
                      : `OpenAI API cost for this generation (${QUALITY_LABEL[quality]} quality, ${frameCount} frame${frameCount > 1 ? 's' : ''})`
                  }
                  className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/65 backdrop-blur-sm text-white text-xs font-semibold shadow-md"
                >
                  <i className={`text-sm ${provider === 'gemini' ? 'ri-google-fill' : 'ri-money-dollar-circle-line'}`} />
                  <span className="tabular-nums">${lastGenCost.toFixed(4)}</span>
                  <span className="text-white/60 font-normal">
                    · {provider === 'gemini' ? 'Gemini' : QUALITY_LABEL[quality]}
                  </span>
                </div>
              )}
            </div>

            {/* Carousel tabs */}
            {isCarousel && (
              <div className="flex items-center justify-center gap-2">
                {generatedImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImageIndex(i)}
                    className={`relative w-14 h-14 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      activeImageIndex === i ? 'border-primary shadow-md' : 'border-transparent hover:border-surface-dark/50'
                    }`}
                  >
                    <img src={img} alt={`Frame ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className={`text-xs font-bold ${activeImageIndex === i ? 'text-primary' : 'text-white drop-shadow-md'}`}>
                        {i + 1}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer panel — sibling of the white content panel above so the
          two stack vertically the same way steps 1-5 do in app/page.tsx.
          Only rendered in the ready state (loading / error own their own
          retry CTAs inside the content panel). */}
      {!generating && !errorView && (
        <div className="flex-shrink-0 mt-4 bg-surface-panel rounded-2xl border-2 border-white px-6 py-4 shadow-lg shadow-primary/15 flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-dark hover:bg-surface transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Back
            </button>

            <div className="flex flex-wrap items-center gap-2.5">
              {/* Regenerate with quality popover */}
              <div className="relative">
                <button
                  onClick={() => setShowRegenMenu((v) => !v)}
                  disabled={!readyForEditing}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-surface-dark hover:border-primary/30 text-dark hover:text-primary text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-base" />
                  Regenerate
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${showRegenMenu ? 'rotate-180' : ''}`} />
                </button>
                {showRegenMenu && (
                  <>
                    {/* Backdrop catches outside-clicks */}
                    <button
                      type="button"
                      aria-label="Close menu"
                      className="fixed inset-0 z-10 cursor-default"
                      onClick={() => setShowRegenMenu(false)}
                    />
                    <div className="absolute bottom-full right-0 mb-2 w-72 z-20 bg-white rounded-2xl border border-surface-dark/40 shadow-xl p-3">
                      <p className="text-xs font-bold text-dark-muted uppercase tracking-wide px-1 mb-2">
                        Pick a quality to regenerate
                      </p>
                      {(['low', 'medium', 'high'] as Quality[]).map((q) => {
                        const cost = QUALITY_MULTIPLIER[q] * frameCount;
                        const isCurrent = q === quality;
                        // Same gate logic as Step 6 (StepQuality): free tier
                        // is locked to 'low' (Regular). Letting users pick
                        // Enhanced/Studio here would 403 from the backend
                        // (defense in depth) — silent failure. Surface the
                        // lock in the UI so the user knows to upgrade.
                        const locked = isFree && q !== 'low';
                        return (
                          <button
                            key={q}
                            onClick={() => !locked && handleRegenerateWithQuality(q)}
                            disabled={locked}
                            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                              locked
                                ? 'opacity-60 cursor-not-allowed'
                                : isCurrent ? 'bg-primary/5 ring-1 ring-primary/30 cursor-pointer' : 'hover:bg-surface cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                                locked
                                  ? 'bg-surface-dark/40 text-dark-muted'
                                  : isCurrent ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
                              }`}>
                                <i className={`text-sm ${
                                  q === 'low' ? 'ri-flashlight-line' : q === 'medium' ? 'ri-star-line' : 'ri-vip-crown-line'
                                }`} />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-dark leading-tight">
                                  {QUALITY_LABEL[q]}
                                </p>
                                {isCurrent && (
                                  <p className="text-xs text-primary font-medium">Current</p>
                                )}
                                {locked && (
                                  <p className="text-xs text-amber-700 font-medium">Upgrade to unlock</p>
                                )}
                              </div>
                            </div>
                            {locked ? (
                              <i className="ri-lock-line text-dark-muted" />
                            ) : (
                              <div className="flex items-center gap-1 text-xs font-bold text-dark">
                                <i className="ri-copper-coin-line text-accent" />
                                {cost}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Save to Gallery */}
              <button
                onClick={handleSaveToGallery}
                disabled={!readyForEditing || saveStatus === 'saving'}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-surface-dark hover:border-primary/30 text-dark hover:text-primary text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {saveStatus === 'idle' && (<><i className="ri-heart-3-line text-base" />Save to My Creatives</>)}
                {saveStatus === 'saving' && (<><i className="ri-loader-4-line text-base animate-spin" />Saving…</>)}
                {saveStatus === 'saved' && (<><i className="ri-check-line text-base text-green-600" />Saved</>)}
                {saveStatus === 'error' && (<><i className="ri-error-warning-line text-base text-red-500" />Try again</>)}
              </button>

              {/* Download — primary CTA. Free tier sees the amber upgrade
                  CTA routing to /checkout; paid tiers get the real download. */}
              {isFree ? (
                <button
                  onClick={() => navigate('/checkout')}
                  title="Free trial doesn't include export — upgrade to download your posts"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-base font-bold transition-colors cursor-pointer shadow-lg shadow-amber-500/20 whitespace-nowrap"
                >
                  <i className="ri-vip-crown-line text-lg" />
                  Upgrade to Download
                </button>
              ) : (
                <button
                  onClick={handleDownload}
                  disabled={!readyForEditing || !activeImage}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-base font-bold transition-colors cursor-pointer shadow-lg shadow-primary/15 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <i className="ri-download-2-line text-lg" />
                  Download
                </button>
              )}

              {/* Continue to text editor */}
              <button
                onClick={onContinue}
                disabled={!readyForEditing}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-surface-dark hover:border-primary/30 text-dark hover:text-primary text-sm font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Add Text
                <i className="ri-arrow-right-line text-base" />
              </button>
            </div>
        </div>
      )}
    </div>
  );
}
